import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../../services/api';
import toast from 'react-hot-toast';
import { Link2, Copy, Send, CheckCircle2, UserPlus, ShieldCheck } from 'lucide-react';

const OnboardingGenerator = () => {
    const [loading, setLoading] = useState(false);
    const [searchParams] = useSearchParams();
    const [formData, setFormData] = useState({
        role: 'doctor',
        doctorType: 'Surgeon',
        title: 'Mr.',
        email: '',
        firstName: '',
        lastName: '',
        phone: '',
        alternativePhone: ''
    });

    useEffect(() => {
        const role = searchParams.get('role');
        const doctorType = searchParams.get('doctorType');
        const email = searchParams.get('email');
        const firstName = searchParams.get('firstName');
        const lastName = searchParams.get('lastName');
        const phone = searchParams.get('phone');

        if (role || email || firstName || lastName || phone) {
            setFormData(prev => ({
                ...prev,
                role: role || prev.role,
                doctorType: doctorType !== null ? doctorType : prev.doctorType,
                email: email || prev.email,
                firstName: firstName || prev.firstName,
                lastName: lastName || prev.lastName,
                phone: phone || prev.phone,
                title: role === 'doctor' ? 'Dr.' : 'Mr.'
            }));
        }
    }, [searchParams]);
    const [generatedLink, setGeneratedLink] = useState(null);

    const handleGenerate = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const response = await api.post('/onboarding/generate-link', formData);
            if (response.data.success) {
                const token = response.data.data.token;
                const role = formData.role;
                const doctorType = formData.doctorType;
                const fullLink = `${window.location.origin}/onboarding-form/${token}?role=${role}${doctorType ? `&doctorType=${doctorType}` : ''}`;

                setGeneratedLink(fullLink);
                toast.success('Onboarding link generated!');
            }
        } catch (err) {
            toast.error(err.response?.data?.message || 'Generation failed');
        } finally {
            setLoading(false);
        }
    };

    const copyToClipboard = () => {
        navigator.clipboard.writeText(generatedLink);
        toast.success('Link copied to clipboard!');
    };

    return (
        <div className="max-w-4xl mx-auto space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
            <div className="bg-white rounded-[40px] shadow-2xl shadow-blue-900/10 border border-gray-100 overflow-hidden">
                <div className="bg-[#143E78] p-10 text-white relative">
                    <div className="relative z-10">
                        <div className="flex items-center gap-2 mb-3">
                            <div className="p-1.5 sm:p-3 bg-white/10 rounded-lg sm:rounded-2xl backdrop-blur-md border border-white/10">
                                <UserPlus className="w-4 h-4 sm:w-6 sm:h-6 text-blue-300" />
                            </div>
                            <span className="text-[8px] sm:text-[10px] font-black uppercase tracking-[0.2em] text-blue-200">Generate Onboarding Link</span>
                        </div>
                        <h1 className="text-2xl sm:text-4xl font-black tracking-tight mb-2">Invite New Partners</h1>
                        <p className="text-xs sm:text-base text-blue-100/70 font-medium max-w-xl leading-relaxed">Generate a secure onboarding gateway for surgeons, hospitals, or Lead Partners to join the DOXEZ ecosystem.</p>
                    </div>

                    {/* Background decor */}
                    <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full blur-3xl -mr-20 -mt-20"></div>
                </div>

                <div className="p-10">
                    <form onSubmit={handleGenerate} className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div className="space-y-1.5">
                            <label className="text-[9px] font-black uppercase text-gray-400 tracking-widest px-1">Partner Category</label>
                            <select
                                className="w-full bg-gray-50 border border-gray-100 rounded-xl sm:rounded-2xl px-4 py-3 sm:py-4 font-bold text-gray-700 text-xs sm:text-sm focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all appearance-none cursor-pointer"
                                value={formData.role === 'doctor' ? `doctor:${formData.doctorType}` : formData.role}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    if (val.startsWith('doctor:')) {
                                        const [role, doctorType] = val.split(':');
                                        setFormData({ ...formData, role, doctorType, title: 'Dr.' });
                                    } else {
                                        setFormData({ ...formData, role: val, doctorType: '', title: 'Mr.' });
                                    }
                                }}
                            >
                                <option value="doctor:Surgeon">Surgeon</option>
                                <option value="doctor:anesthesiologist">Anaesthesiologist</option>
                                <option value="hospital">Medical Center / Hospital</option>
                                <option value="marketing_agent">Lead Partner</option>

                            </select>
                        </div>

                        <div className="space-y-1.5">
                            <label className="text-[9px] font-black uppercase text-gray-400 tracking-widest px-1">Primary Email</label>
                            <input
                                type="email"
                                required
                                placeholder="partner@example.com"
                                className="w-full bg-gray-50 border border-gray-100 rounded-xl sm:rounded-2xl px-4 py-3 sm:py-4 font-bold text-gray-700 text-xs sm:text-sm focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all"
                                value={formData.email}
                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            />
                        </div>

                        {formData.role === 'hospital' ? (
                            <div className="md:col-span-2 space-y-1.5">
                                <label className="text-[9px] font-black uppercase text-gray-400 tracking-widest px-1">Hospital / Medical Center Name <span className="text-red-500">*</span></label>
                                <input
                                    type="text"
                                    required
                                    placeholder="Enter complete facility name"
                                    className="w-full bg-gray-50 border border-gray-100 rounded-xl sm:rounded-2xl px-4 py-3 sm:py-4 font-bold text-gray-700 text-xs sm:text-sm focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all"
                                    value={formData.firstName}
                                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value, lastName: '', title: '' })}
                                />
                            </div>
                        ) : (
                            <>
                                <div className="md:col-span-1 grid grid-cols-1 sm:grid-cols-4 gap-3">
                                    <div className="col-span-1 space-y-1.5">
                                        <label className="text-[9px] font-black uppercase text-gray-400 tracking-widest px-1">Title</label>
                                        <select
                                            className="w-full bg-gray-50 border border-gray-100 rounded-xl sm:rounded-2xl px-3 py-3 sm:py-4 font-bold text-gray-700 text-xs sm:text-sm focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all appearance-none cursor-pointer"
                                            value={formData.title}
                                            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                        >
                                            <option value="Mr.">Mr.</option>
                                            <option value="Ms.">Ms.</option>
                                            <option value="Mrs.">Mrs.</option>
                                            <option value="Miss.">Miss.</option>
                                            <option value="Dr.">Dr.</option>
                                        </select>
                                    </div>
                                    <div className="col-span-3 space-y-1.5">
                                        <label className="text-[9px] font-black uppercase text-gray-400 tracking-widest px-1">First Name (Optional)</label>
                                        <input
                                            type="text"
                                            placeholder="First Name"
                                            className="w-full bg-gray-50 border border-gray-100 rounded-xl sm:rounded-2xl px-4 py-3 sm:py-4 font-bold text-gray-700 text-xs sm:text-sm focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all"
                                            value={formData.firstName}
                                            onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                                        />
                                    </div>
                                </div>

                                <div className="space-y-1.5">
                                    <label className="text-[9px] font-black uppercase text-gray-400 tracking-widest px-1">Last Name <span className="text-red-500">*</span></label>
                                    <input
                                        type="text"
                                        required
                                        placeholder="Candidate Surname"
                                        className="w-full bg-gray-50 border border-gray-100 rounded-xl sm:rounded-2xl px-4 py-3 sm:py-4 font-bold text-gray-700 text-xs sm:text-sm focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all"
                                        value={formData.lastName}
                                        onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                                    />
                                </div>
                            </>
                        )}

                        <div className="space-y-1.5">
                            <label className="text-[9px] font-black uppercase text-gray-400 tracking-widest px-1">Primary Phone (WhatsApp)</label>
                            <input
                                type="text"
                                placeholder="10-digit mobile"
                                className="w-full bg-gray-50 border border-gray-100 rounded-xl sm:rounded-2xl px-4 py-3 sm:py-4 font-bold text-gray-700 text-xs sm:text-sm focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all"
                                value={formData.phone}
                                onChange={(e) => setFormData({ ...formData, phone: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                                maxLength={10}
                            />
                        </div>

                        <div className="space-y-1.5">
                            <label className="text-[9px] font-black uppercase text-gray-400 tracking-widest px-1">Alternative Phone (Optional)</label>
                            <input
                                type="text"
                                placeholder="Secondary contact"
                                className="w-full bg-gray-50 border border-gray-100 rounded-xl sm:rounded-2xl px-4 py-3 sm:py-4 font-bold text-gray-700 text-xs sm:text-sm focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all"
                                value={formData.alternativePhone}
                                onChange={(e) => setFormData({ ...formData, alternativePhone: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                                maxLength={10}
                            />
                        </div>

                        <div className="md:col-span-2 pt-2 sm:pt-4">
                            <button
                                type="submit"
                                disabled={loading}
                                className={`w-full bg-[#143E78] hover:bg-blue-800 text-white font-black py-3.5 sm:py-5 rounded-xl sm:rounded-2xl flex items-center justify-center gap-2 sm:gap-3 shadow-xl shadow-blue-900/10 hover:-translate-y-0.5 transition-all disabled:opacity-90 text-xs sm:text-base uppercase tracking-wider ${loading ? 'animate-pulse bg-blue-950 cursor-not-allowed' : ''}`}
                            >
                                {loading ? (
                                    <div className="flex items-center gap-2 sm:gap-3">
                                        {/* Glowing pulsing icon skeleton */}
                                        <div className="w-4 h-4 sm:w-5 sm:h-5 rounded-full bg-white/40 animate-ping shrink-0" />
                                        {/* Pulsing text bar skeleton */}
                                        <div className="h-3 sm:h-4 w-40 sm:w-56 bg-white/20 rounded-md animate-pulse" />
                                    </div>
                                ) : (
                                    <>
                                        <Link2 className="w-4 h-4 sm:w-5 sm:h-5 shrink-0" />
                                        <span className="truncate">GENERATE ENROLLMENT GATEWAY</span>
                                    </>
                                )}
                            </button>
                        </div>
                    </form>

                    {generatedLink && (
                        <div className="mt-6 sm:mt-10 p-5 sm:p-8 bg-blue-50 rounded-2xl sm:rounded-[32px] border border-blue-100 animate-in zoom-in-95 duration-500">
                            <div className="flex items-center gap-2 mb-3 sm:mb-4">
                                <ShieldCheck className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-emerald-500" />
                                <span className="text-[8px] sm:text-[10px] font-black uppercase text-emerald-600 tracking-[0.2em]">Live Secure URL</span>
                            </div>
                            <div className="flex flex-col gap-3 sm:gap-4">
                                <div className="bg-white border border-blue-100 rounded-xl sm:rounded-2xl px-4 py-3 sm:px-6 sm:py-4 font-mono text-[10px] sm:text-sm text-blue-900 break-all w-full leading-relaxed shadow-inner">
                                    {generatedLink}
                                </div>
                                <div className="grid grid-cols-2 sm:flex sm:flex-row gap-2 sm:gap-3 w-full">
                                    <button
                                        onClick={copyToClipboard}
                                        className="bg-white hover:bg-blue-50 text-[#143E78] border border-blue-200 font-bold px-3 py-3 sm:px-6 sm:py-4 rounded-xl sm:rounded-2xl flex items-center justify-center gap-1.5 sm:gap-2 transition-all active:scale-[0.98] shadow-sm text-[10px] sm:text-sm"
                                    >
                                        <Copy className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> Copy
                                    </button>
                                    <button
                                        disabled={loading}
                                        className="bg-emerald-500 hover:bg-emerald-600 text-white font-bold px-3 py-3 sm:px-6 sm:py-4 rounded-xl sm:rounded-2xl flex items-center justify-center gap-1.5 sm:gap-2 transition-all shadow-lg shadow-emerald-500/20 active:scale-[0.98] disabled:opacity-50 text-[10px] sm:text-sm"
                                        onClick={async () => {
                                            setLoading(true);
                                            try {
                                                await api.post('/onboarding/resend-link', { email: formData.email });
                                                toast.success('Dispatched');
                                            } catch (err) {
                                                toast.error('Failed');
                                            } finally {
                                                setLoading(false);
                                            }
                                        }}
                                    >
                                        <Send className="w-4 h-4" /> {loading ? 'Sending...' : 'Send via Cloud'}
                                    </button>
                                </div>
                            </div>
                            <p className="mt-4 text-[9px] sm:text-[10px] text-gray-400 font-bold italic text-center underline decoration-blue-100 underline-offset-4">This link will allow the candidate to submit their credentials.</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default OnboardingGenerator;
